import { useEffect, useState } from 'react';
import { getMe, getMemberState, type Me, type QuizSubmitResult } from '../../api/endpoints.js';
import { useApi } from '../../api/hooks.js';
import {
  isCheckpointStep,
  isFlowStep,
  readFlags,
  resumeStep,
  writeFlags,
  type FlowStep,
} from '../../flow/machine.js';
import { goHome, goStep } from '../../flow/nav.js';
import { Failure } from '../../ui/kit.js';
import { BrandPulse, FlowScreen, FlowSub, FlowTitle, PrimaryButton } from '../../ui/flow.js';
import { PurposeStep, InfoStep, AssessmentStep } from './purpose.js';
import { QuizStep, CalculatingStep, ScoreStep } from './quiz.js';
import { PlansStep, SkipPlanStep } from './plan.js';
import { ProfileStep, FaceIntroStep } from './identity.js';
import { ConsentGateStep } from './consent.js';
import { SelfieStep } from './selfie.js';
import { ProfileExtraStep, PhotosStep, NotificationsStep } from './extras.js';
import { FamilyChoiceStep, FamilyMemberStep, FamilyPhotosStep, type MemberContext } from './family.js';

/**
 * The wizard controller — the mobile AppNavigator's onboarding stack plus its
 * Splash router in one place. The step lives in the hash (#/setup/<step>) so
 * the browser's Back walks the same sequence a stack navigator would; what a
 * step needs handed across (quiz answers, the fresh score) lives here in
 * memory, and a reload that loses it simply resumes from the server's own
 * onboarding state, exactly like the mobile relaunch.
 *
 * THE MEMBER IS THE ONE EXCEPTION, and it used to be the same in-memory
 * hand-off as the rest. It could not be: a reload mid-loop, or arriving from
 * the Family tab at all, left `member` null and the loop restarted at "add a
 * member" — which is how the Family tab came to abandon the very member it
 * created. So `family-member` accepts the person in the hash
 * (`#/setup/family-member/<personId>`) and the context is rebuilt from
 * `GET /v1/household/members/{personId}`, which carries every field
 * MemberContext holds. The server already knew where they were up to; this is
 * what lets it say so.
 */

function hasProfileExtras(me: Me): boolean {
  return me.person.employer_name !== null || me.person.school_name !== null;
}

export function OnboardingFlow({ stepId }: { stepId: string }) {
  // `<step>` or `<step>/<personId>`. Only the member loop uses the tail, and
  // the person id is authorised by the api on every call it is sent on — it is
  // an input we check, never a way to address data.
  const slash = stepId.indexOf('/');
  const stepName = slash === -1 ? stepId : stepId.slice(0, slash);
  const seedPersonId = slash === -1 ? '' : decodeURIComponent(stepId.slice(slash + 1));

  const me = useApi(getMe, []);
  const [pendingQuiz, setPendingQuiz] = useState<{
    quizVersion: string;
    answers: Record<string, string | string[]>;
  } | null>(null);
  const [score, setScore] = useState<QuizSubmitResult | null>(null);
  const [member, setMember] = useState<MemberContext | null>(null);

  const step: FlowStep | null = isFlowStep(stepName) ? stepName : null;

  /**
   * Rebuilding the member from the hash. Skipped once we hold one, so
   * advancing through the loop does not refetch, and skipped entirely when
   * there is no id in the hash — the create-a-new-member path is unchanged.
   */
  const seed = useApi(() => getMemberState(seedPersonId), [seedPersonId], {
    skip: seedPersonId === '' || member !== null,
  });
  const seeded = seed.data;
  useEffect(() => {
    if (seeded === null || seeded === undefined) return;
    setMember({
      personId: seeded.person_id,
      firstName: seeded.first_name ?? 'them',
      lastName: seeded.last_name ?? '',
      isMinor: seeded.is_minor,
    });
  }, [seeded]);

  // The router: an unknown step, or a hand-off step whose in-memory cargo is
  // gone (a reload), resolves to wherever the server + local flags say we are.
  useEffect(() => {
    if (me.data === null) return;
    const needsRouting =
      step === null ||
      (step === 'calculating' && pendingQuiz === null) ||
      (step === 'score' && score === null);
    if (!needsRouting) return;
    const target = resumeStep(me.data.onboarding, hasProfileExtras(me.data), readFlags());
    if (target === 'done') goHome();
    else goStep(target, { replace: true });
  }, [me.data, step, pendingQuiz, score]);

  // The checkpoint, exactly as the mobile screens 3–8 keep it: written on every
  // focus within the pre-write block, cleared at the selfie (point of no
  // return) — a later relaunch resumes into the block instead of its start.
  useEffect(() => {
    if (step === null) return;
    if (isCheckpointStep(step)) writeFlags({ checkpoint: step });
    if (step === 'selfie') writeFlags({ checkpoint: null });
  }, [step]);

  if (me.error !== null) {
    return (
      <FlowScreen>
        <Failure error={me.error} />
        <PrimaryButton onClick={me.reload}>Try again</PrimaryButton>
      </FlowScreen>
    );
  }
  if (me.data === null) {
    return (
      <FlowScreen>
        <div className="flow-center">
          <BrandPulse label={'Hang tight, we’re setting up your experience'} />
        </div>
      </FlowScreen>
    );
  }

  switch (step) {
    case 'purpose':
      return <PurposeStep />;
    case 'info':
      return <InfoStep />;
    case 'assessment':
      return <AssessmentStep />;
    case 'quiz':
      return (
        <QuizStep
          onComplete={(quizVersion, answers) => {
            setPendingQuiz({ quizVersion, answers });
            goStep('calculating', { replace: true });
          }}
        />
      );
    case 'calculating':
      return (
        <CalculatingStep
          pending={pendingQuiz}
          onScored={(fresh) => {
            setScore(fresh);
            setPendingQuiz(null);
            me.reload();
            goStep('score', { replace: true });
          }}
        />
      );
    case 'score':
      if (score === null) return null;
      // A recompute can fail after the answers are durably stored (a real
      // API behaviour, not a client fabrication): the response is stored but
      // score is null, and the honest render is "being worked out", never a
      // crash or an invented number.
      if (score.score === null) {
        return (
          <FlowScreen onBack={() => window.history.back()} onClose={goHome}>
            <div className="flow-center">
              <FlowTitle>Your score is being worked out</FlowTitle>
              <FlowSub>
                We saved your answers. Your Likeness Health Score℠ will be ready shortly — check
                back from your dashboard.
              </FlowSub>
              <PrimaryButton onClick={goHome}>Go to dashboard</PrimaryButton>
            </div>
          </FlowScreen>
        );
      }
      return <ScoreStep score={{ score: score.score, scope_note: score.scope_note }} />;
    case 'plans':
      return <PlansStep />;
    case 'skip-plan':
      return <SkipPlanStep />;
    case 'profile':
      return <ProfileStep me={me.data} reloadMe={me.reload} />;
    case 'face-intro':
      return <FaceIntroStep />;
    case 'consent':
      return <ConsentGateStep me={me.data} reloadMe={me.reload} />;
    case 'selfie':
      return <SelfieStep me={me.data} reloadMe={me.reload} />;
    case 'profile-extra':
      return <ProfileExtraStep me={me.data} reloadMe={me.reload} />;
    case 'photos':
      return <PhotosStep />;
    case 'notifications':
      return <NotificationsStep />;
    case 'family-choice':
      return <FamilyChoiceStep />;
    case 'family-member':
      // A deep link that has not resolved yet must NOT fall through to the
      // details form: that would offer to create a second person for someone
      // who already exists.
      if (seedPersonId !== '' && member === null) {
        return (
          <FlowScreen>
            {seed.error !== null ? (
              <Failure error={seed.error} />
            ) : (
              <div className="flow-center">
                <BrandPulse />
              </div>
            )}
          </FlowScreen>
        );
      }
      return <FamilyMemberStep member={member} onMember={setMember} />;
    case 'family-photos':
      return <FamilyPhotosStep member={member} />;
    case null:
      return null;
  }
}
